import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../api/apiClient';

export type MediaType = 'banner' | 'post';
export type MediaStatus = 'PENDING' | 'approved' | 'rejected' | 'archived';

export interface MediaItem {
  id: number;
  application_id: string;
  type: MediaType;
  status: MediaStatus;
  image: {
    width: number;
    height: number;
    url: string;
  };
  created_at: string;
}

interface MediasResponse {
  medias: MediaItem[];
}

const fetchMedias = async (
  publisherId: string,
  applicationId: string,
): Promise<MediasResponse> => {
  const response = await apiClient.get(
    `/v2/publisher/medias?publisher_id=${publisherId}&application_id=${applicationId}`,
  );
  return response.data;
};

export const useMediasList = (publisherId: string, applicationId: string) => {
  return useQuery({
    queryKey: ['medias', publisherId, applicationId],
    queryFn: () => fetchMedias(publisherId, applicationId),
    staleTime: 2 * 60 * 1000,
    retry: 2,
    refetchOnWindowFocus: false,
    enabled: !!publisherId && !!applicationId,
  });
};

export const useUploadMedia = (publisherId: string, applicationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      file,
      type,
    }: {
      file: File;
      type: MediaType;
    }) => {
      const formData = new FormData();
      formData.append('publisher_id', publisherId);
      formData.append('application_id', applicationId);
      formData.append('type', type);
      formData.append('image', file);
      const response = await apiClient.post('/v2/publisher/medias', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['medias', publisherId, applicationId],
      });
    },
  });
};

export const useArchiveMedia = (publisherId: string, applicationId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: number) => {
      const formData = new FormData();
      formData.append('publisher_id', publisherId);
      formData.append('application_id', applicationId);
      const response = await apiClient.post(
        `/v2/publisher/medias/${mediaId}/archive`,
        formData,
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['medias', publisherId, applicationId],
      });
    },
  });
};
